import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form2290CreditVehicles = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Claiming Credit on Form 2290: Guide to HVUT Credits"
          description="Learn how to claim credits on Form 2290 & obtain HVUT refunds. Discover eligibility, process, and tips for reducing heavy vehicle tax liability."
          keywords="Form 2290 credits, Form 2290 Sold credit, Low Mileage Vehicles, 2290 Low Mileage credits, Credit Vehicles in Form 2290"
          canonical="resources/excise-tax/form-2290-credit-vehicles/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Claiming Credit</span>
          </span>{' '}
          on Form 2290
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/form-2290-credit-vehicles/"
                      />

                      {/* Article image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: {
                            md: 20,
                            sm: 18,
                            xs: 16,
                          },
                          color: '#000',
                        }}
                      >
                        Form 2290 Credit Vehicles at a Glance
                      </Typography>

                      <Box
                        component="ul"
                        itemScope
                        itemType="https://schema.org/ItemList"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        {/* Credit Eligibility */}
                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="1"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            Form 2290 Credits can be claimed by heavy vehicle
                            owners who have already paid the Heavy Vehicle Use
                            Tax (HVUT) if their vehicle was{' '}
                            <strong style={{ fontWeight: 600 }}>
                              sold, stolen, destroyed, or driven 5,000 miles or
                              less
                            </strong>{' '}
                            during the tax period (7,500 miles or less for
                            agricultural vehicles).
                          </span>
                        </li>

                        {/* Eligibility */}
                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="2"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Eligibility:
                            </strong>{' '}
                            Credits may be claimed on your next Form 2290 if the
                            vehicle qualifies under IRS rules. If your credit
                            exceeds the tax due, you may claim the remaining
                            amount by filing{' '}
                            <strong style={{ fontWeight: 600 }}>
                              Form 8849, Schedule 6.
                            </strong>
                          </span>
                        </li>

                        {/* Requirements */}
                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="3"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Requirements:
                            </strong>{' '}
                            To claim a credit, you must provide the required
                            vehicle details, including the VIN (Vehicle
                            Identification Number), taxable gross weight, and
                            supporting information related to the qualifying
                            event.
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>

                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <Link
                        href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        IRS Form 2290
                      </Link>
                      , also known as the Heavy Highway Vehicle Use Tax Return,
                      is an annual tax return filed by owners or operators of
                      heavy vehicles with a{' '}
                      <Link
                        href="/resources/excise-tax/form-2290-taxable-gross-weight-and-weight-categories/"
                        sx={{ color: '#0202ff' }}
                      >
                        gross weight
                      </Link>{' '}
                      of 55,000 pounds or more.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      While the primary purpose of this form is to report and
                      pay the Heavy Vehicle Use Tax (HVUT), the IRS also
                      provides a way to claim credits on your Form 2290 if your
                      heavy vehicle was stolen, destroyed, sold, or driven under
                      5,000 miles.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      In this resource, we&apos;ll learn about IRS Form 2290,
                      who qualifies for a Form 2290 credit, the conditions under
                      which you can claim credits, and how to claim credits in
                      <Link
                        href="/resources/excise-tax/form-2290-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        2290 tax form
                      </Link>
                      .
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#about" sx={{ color: '#0202ff' }}>
                          About IRS Form 2290
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#credit" sx={{ color: '#0202ff' }}>
                          Credit Vehicles in Form 2290
                        </Link>
                        <ul style={{ listStyleType: 'square' }}>
                          <li>
                            <Link href="#reasons" sx={{ color: '#0202ff' }}>
                              Reasons Under Which You Can Claim Credits
                            </Link>
                          </li>
                          <li>
                            <Link href="#conditions" sx={{ color: '#0202ff' }}>
                              Conditions for Claiming Credit in Form 2290
                            </Link>
                          </li>
                        </ul>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#important" sx={{ color: '#0202ff' }}>
                          Important Notes on Form 2290 Claims
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#claim" sx={{ color: '#0202ff' }}>
                          Claim Credits on Form 2290 with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="about"
                >
                  About HVUT Form 2290
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>HVUT Form 2290</strong>&nbsp;is an IRS tax form that
                  owners or operators of heavy vehicles must file with a gross
                  weight of 55,000 pounds or more. It is an annual tax imposed
                  on heavy vehicles used on public highways to support the
                  maintenance and improvement of the country&apos;s
                  transportation infrastructure.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The{' '}
                  <Link
                    href="/tools/form-2290-tax-calculator/"
                    sx={{ color: '#0202ff' }}
                  >
                    HVUT tax amount
                  </Link>{' '}
                  is calculated based on the vehicle&apos;s gross weight and the
                  number of miles it is expected to be driven during the tax
                  period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  However, vehicles that are used for less than 5,000 miles
                  (7,500 miles for agricultural vehicles) during the tax period
                  may be exempt from the HVUT taxes but are required to file
                  Form 2290.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="credit"
                >
                  Credit Vehicles in Form 2290
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Credit vehicles, also referred to as creditable vehicles, are
                  those for which credits can be claimed on Form 2290. These
                  vehicles may include those that were sold, stolen, destroyed,
                  or used for minimal mileage during the tax period.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0,
                    lineHeight: 1.5,
                    scrollMarginTop: 87,
                  }}
                  id="reasons"
                >
                  Reasons Under Which You Can Claim Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS allows taxpayers to claim credits on Form 2290 under
                  specific circumstances, including:
                </Typography>
                <ol>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Sold, Destroyed, or Stolen Vehicles
                    </span>
                    <br />
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      If a heavy vehicle was sold, destroyed, or stolen during
                      the tax period, a credit can be claimed for the portion of
                      the tax corresponding to the period after the event
                      occurred.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      For example, you reported your taxable vehicle and paid
                      $550 from July 1st, 2025, to June 30th, 2026, and later
                      sold your vehicle in December of 2026. You are now
                      eligible to claim a credit on your 2290 for the partial
                      months that you didn’t use (6 months) in the following tax
                      year of July 1st, 2026, to June 30th, 2027.
                    </Typography>
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{
                        mb: 5,
                        mt: 0,
                        borderLeft: '3px solid rgb(102, 60, 0)',
                      }}
                    >
                      <b>Note:</b> If your vehicle is sold, destroyed, or
                      stolen, please include the VIN, Taxable Gross Weight of
                      the Vehicle, and Date of Theft, Accident, or Sale while
                      reporting those vehicles.
                      <br />
                      <br />
                      For sold vehicles, you must include the buyer&apos;s
                      details, or else you will not be able to submit and claim
                      credit on your Form 2290.
                    </Alert>
                  </li>
                  <li>
                    <b>Low Mileage Vehicles</b>
                    <br />
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      The Low Mileage Credit is a valuable tax incentive as it
                      can significantly reduce the amount of taxes truck owner
                      needs to pay on their vehicles.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Vehicles that have been used for less than 5,000 miles
                      (7,500 miles or less for agricultural vehicles) during any
                      taxable period may qualify for a low mileage credit. This
                      mileage threshold exempts the vehicles from complete tax
                      liability, allowing taxpayers to claim a credit for the
                      remaining tax period.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 5 }}>
                      To claim low mileage credit, the mileage must be recorded
                      through receipts and other forms of proof. Additionally,
                      the vehicle must have been registered and used in the same
                      state for at least one year before filing Form 2290.
                    </Typography>
                  </li>
                </ol>
                <Box
                  component="div"
                  sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '18px !important',
                      }}
                    >
                      Report your credit vehicles on Form 2290 and claim credits
                      with TaxZerone
                    </Typography>
                    <Typography variant="body2">
                      E-file Form 2290 with TaxZerone and claim your credits for
                      the excise tax amount easily!
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290credit`}
                    >
                      Claim Credits on Form 2290
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0,
                    lineHeight: 1.5,
                    scrollMarginTop: 87,
                  }}
                  id="conditions"
                >
                  Conditions for Claiming Credit in Form 2290
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To claim credits on Form 2290, taxpayers must meet certain
                  conditions and provide the necessary documentation. These
                  conditions may include:
                </Typography>
                <ul>
                  <li>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <b>Providing Proof of Sale:</b> When claiming credit for a
                      sold vehicle, taxpayers must provide documentation such as
                      a bill of sale or transfer of ownership to support the
                      claim.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <b>Reporting Stolen or Destroyed Vehicles:</b> In the case
                      of stolen or destroyed vehicles, taxpayers should report
                      these incidents to the appropriate authorities and retain
                      relevant documentation, such as police reports or
                      insurance records, to substantiate the credit claim.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 5 }}>
                      <b>Maintaining Accurate Records:</b> It is crucial to
                      maintain accurate records of vehicle usage, mileage, and
                      any relevant events that may qualify for a credit. Proper
                      documentation ensures the credibility of credit claims and
                      compliance with IRS requirements.
                    </Typography>
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="important"
                >
                  Important Notes on Form 2290 Claims
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below are a few necessary things to remember before you claim
                  credits on Form 2290.
                </Typography>
                <ul>
                  <li>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      The claimed credit amount must not exceed the reported tax
                      amount on the IRS Form 2290. If there is an excess in
                      credit, it must be claimed using{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-8849-schedule-6-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 8849
                      </Link>
                      ,{' '}
                      <i>
                        Claim for Refund of Excise Taxes and Schedule 6, Other
                        Claims
                      </i>
                      .
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      You cannot claim the credit until the end of the tax
                      period for the vehicles for which the taxes are paid and
                      used 5,000 miles or less (7,500 miles or less for
                      agricultural vehicles).
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      You cannot claim a credit, lower tax, exemption, or refund
                      if you reported your taxable vehicles at one weight but
                      occasionally operated at a lower weight class.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 5 }}>
                      You need to claim the credit within three years of filing
                      the actual 2290 return to which the claim relates or two
                      years from the tax reported and paid, whichever is later.
                    </Typography>
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="claim"
                >
                  Claim Credits on Form 2290 with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Understanding the importance of claiming credits on Form 2290
                  is essential for heavy vehicle owners as it provides them an
                  opportunity to maximize their tax benefits while fulfilling
                  tax obligations for heavy highway vehicle use.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  By meeting the conditions set by the IRS and maintaining
                  accurate records, taxpayers can make appropriate credit claims
                  on Form 2290.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  With{' '}
                  <Link href="/" sx={{ color: '#0202ff' }}>
                    TaxZerone
                  </Link>
                  , you can claim credits for excise taxes while e-filing your
                  HVUT Form 2290 in 3 simple steps!
                </Typography>
                <ol>
                  <li>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Enter the required information, such as your business
                      name, EIN, VIN, taxable gross weight, and first used month
                      (FUM). Now, under the Credit Vehicles section, select a
                      reason for claiming credit, and provide the required
                      details. TaxZerone will automatically calculate your
                      credit amount and tax due based on your input for the tax
                      year.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Review the information you provided in the form, check and
                      rectify errors, and pay the balance tax due using EFW,
                      EFTPS, credit/debit card option.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 5 }}>
                      Transmit the return to the IRS and get your stamped
                      Schedule 1 as soon as the IRS processes it.
                    </Typography>
                  </li>
                </ol>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Claim your credits by e-filing IRS Form 2290 with TaxZerone
            </Typography>
            {/* <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              .
            </Typography> */}

            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
              }}
            >
              starting at just <b>$19.99/return</b>
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290credit`}
              >
                E-file Form 2290
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form2290CreditVehicles;
